/**
 * Release Barcode — one string in the UI, two properties in the data.
 *
 * The pipeline stores a release's barcode as `upc` (12 digits, with `ean` set
 * to its zero-padded 13-digit form) or as `ean` alone (13 digits). The backend
 * splits a submitted `barcode` onto those two on write, and everything that
 * reads one back uses `upc || ean`, matching the CSV export. Keep this module
 * pure (no React) so every surface can import it.
 */

export const BARCODE_ERROR = 'Barcode must be 12 (UPC) or 13 (EAN) digits';

/** A release's barcode from its stored `upc` / `ean` properties. */
export const releaseBarcode = (
  release: { upc?: string | null; ean?: string | null } | null | undefined
): string => release?.upc || release?.ean || '';

/** A barcode as the backend will store it: spaces and hyphens stripped. */
export const normaliseBarcode = (value: string | null | undefined): string =>
  (value ?? '').replace(/[\s-]/g, '');

/**
 * Validate an optional barcode. Spaces and hyphens are ignored (the backend
 * strips them too); what is left must be 12 or 13 digits. Empty is valid.
 */
export const validateBarcode = (
  value: string | null | undefined
): string | null => {
  const digits = normaliseBarcode(value);
  if (!digits) return null;
  return /^\d{12,13}$/.test(digits) ? null : BARCODE_ERROR;
};
